/**
 * Query observer — the single sink for database queries crossing the backend
 * boundary, and the surface tests read.
 *
 * Wraps {@link createNPlusOneDetector} with environment gating, structured
 * logging of detected N+1 bursts and slow queries, and a subscription hook.
 * Recording is on in development and under Vitest; production builds skip the
 * per-query wrapper unless `VITE_DB_QUERY_ANALYSIS=1` opts in.
 */

import { createBoundedList } from "@/lib/db/bounded-list";
import { createNPlusOneDetector, type NPlusOneReport } from "@/lib/db/n-plus-one-detector";
import type { ObservedQuery } from "@/lib/db/query-shape";
import { logger } from "@/lib/logging";

const DEFAULT_SLOW_QUERY_MS = 750;
const DEFAULT_HISTORY_LIMIT = 200;

export type ReportListener = (report: NPlusOneReport) => void;

/**
 * Marks the observed query as settled with its measured duration. Queries are
 * recorded when they are issued, so N+1 analysis never depends on when a
 * response arrives; the duration arrives later for slow-query reporting.
 */
export type QueryCompletion = (durationMs: number) => void;

export interface QueryObserver {
  /** N+1 reports emitted so far, oldest first. */
  nPlusOneReports(): readonly NPlusOneReport[];
  /** Queries recorded so far, oldest first. */
  queries(): readonly ObservedQuery[];
  record(query: ObservedQuery): QueryCompletion;
  /** Clears recorded queries, reports, and burst state. */
  reset(): void;
  /** Slow queries recorded so far, oldest first. */
  slowQueries(): readonly ObservedQuery[];
  /** Registers a listener for N+1 reports; returns an unsubscribe function. */
  subscribe(listener: ReportListener): () => void;
}

export interface QueryObserverOptions {
  /** Max queries, reports, and slow queries retained. Default 200. */
  readonly historyLimit?: number;
  /** Emit structured warnings through the app logger. */
  readonly log?: boolean;
  /**
   * Duration (ms) at or above which a single query is logged as slow. Defaults
   * to `VITE_DB_SLOW_QUERY_MS`, then 750.
   */
  readonly slowQueryMs?: number;
  /**
   * Signatures or table/rpc names whose repeats are known-good and must never
   * report. Defaults to `VITE_DB_QUERY_ANALYSIS_IGNORE` (comma-separated).
   */
  readonly suppressed?: readonly string[];
  /** Repeats of one signature inside the window that count as N+1. Default 5. */
  readonly threshold?: number;
  /** Rolling window in milliseconds. Default 1000. */
  readonly windowMs?: number;
}

/** Reads Vite env statically so the flag folds away in production builds. */
function readEnv(): {
  analysis?: string;
  ignore?: string;
  mode?: string;
  isDev?: boolean;
  slowQueryMs?: string;
} {
  try {
    return {
      analysis: import.meta.env.VITE_DB_QUERY_ANALYSIS,
      ignore: import.meta.env.VITE_DB_QUERY_ANALYSIS_IGNORE,
      isDev: import.meta.env.DEV,
      mode: import.meta.env.MODE,
      slowQueryMs: import.meta.env.VITE_DB_SLOW_QUERY_MS,
    };
  } catch {
    return {};
  }
}

function parseBoolean(value: string | undefined): boolean | undefined {
  const raw = value?.trim().toLowerCase();
  if (raw === "1" || raw === "true") {
    return true;
  }
  if (raw === "0" || raw === "false") {
    return false;
  }
  return;
}

function isTestEnv(): boolean {
  return readEnv().mode === "test";
}

function isDevOrTest(): boolean {
  const { isDev, mode } = readEnv();
  return isDev === true || mode === "test" || mode === "development";
}

/**
 * Whether queries are recorded and analyzed. `VITE_DB_QUERY_ANALYSIS` wins
 * (`1`/`true` forces on, `0`/`false` forces off, anything else falls through);
 * otherwise development and test builds record and production builds do not.
 */
export function isQueryAnalysisEnabled(): boolean {
  return parseBoolean(readEnv().analysis) ?? isDevOrTest();
}

function resolveSlowQueryMs(): number {
  const parsed = Number(readEnv().slowQueryMs);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_SLOW_QUERY_MS;
}

/** Parses the comma-separated allowlist of known-good repeated queries. */
function resolveSuppressions(): readonly string[] {
  return (readEnv().ignore ?? "")
    .split(",")
    .map((pattern) => pattern.trim())
    .filter((pattern) => pattern.length > 0);
}

export function createQueryObserver(options: QueryObserverOptions = {}): QueryObserver {
  const slowQueryMs = options.slowQueryMs ?? resolveSlowQueryMs();
  const historyLimit = options.historyLimit ?? DEFAULT_HISTORY_LIMIT;
  const shouldLog = options.log ?? false;
  const listeners = new Set<ReportListener>();
  const slow = createBoundedList<ObservedQuery>(historyLimit);

  /** Diagnostics must never break the query that produced them. */
  function warnSafely(payload: Record<string, unknown>, message: string): void {
    if (!shouldLog) {
      return;
    }
    try {
      logger.warn(payload, message);
    } catch {
      // A failing logger must not propagate into data access.
    }
  }

  const detector = createNPlusOneDetector({
    threshold: options.threshold,
    windowMs: options.windowMs,
    historyLimit: options.historyLimit,
    suppressed: options.suppressed ?? resolveSuppressions(),
    onReport(report) {
      warnSafely(
        {
          count: report.count,
          filterColumns: report.filterColumns,
          operation: report.operation,
          remediation: report.remediation,
          signature: report.signature,
          table: report.table,
          windowMs: report.windowMs,
        },
        "[db] N+1 query pattern detected",
      );
      for (const listener of listeners) {
        try {
          listener(report);
        } catch {
          // One failing subscriber must not starve the others.
        }
      }
    },
  });

  function reportSlowQuery(query: ObservedQuery): void {
    slow.push(query);
    warnSafely(
      {
        durationMs: Math.round(query.durationMs ?? 0),
        filterColumns: query.filterColumns,
        operation: query.operation,
        table: query.table,
        thresholdMs: slowQueryMs,
      },
      "[db] slow query",
    );
  }

  return {
    nPlusOneReports() {
      return detector.reports();
    },

    queries() {
      return detector.queries();
    },

    record(query) {
      detector.record(query);
      if (query.durationMs !== undefined && query.durationMs >= slowQueryMs) {
        reportSlowQuery(query);
        return () => {
          // Duration already known — nothing left to complete.
        };
      }
      return (durationMs) => {
        query.durationMs = durationMs;
        if (durationMs >= slowQueryMs) {
          reportSlowQuery(query);
        }
      };
    },

    reset() {
      detector.reset();
      slow.clear();
    },

    slowQueries() {
      return slow.snapshot();
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

/**
 * Process-wide observer used by the backend boundary. Logging stays off under
 * Vitest so suites are quiet; tests read the recorded queries instead (see
 * `src/test-utils/query-analysis.ts`).
 */
export const queryObserver: QueryObserver = createQueryObserver({
  log: isQueryAnalysisEnabled() && !isTestEnv(),
});
